import React, { useState, useEffect, useCallback } from 'react';
import { X, TrendingDown, ChevronLeft, ChevronRight, ChevronDown, Package, DollarSign, Calendar, Receipt } from 'lucide-react';

interface LedgerItem {
  id: number;
  productName: string;
  category: string;
  unitPrice: number;
  qty: number;
  lineTotal: number;
  costPriceSnapshot: number | null;
  netProfit: number | null;
  commissionAmount: number;
}

interface LedgerTransaction {
  id: number;
  txRef: string;
  createdAt: string;
  cashierName: string;
  total: number;
  itemCount: number;
  totalNetProfit: number;
  totalCommission: number;
  items: LedgerItem[];
}

interface LedgerResponse {
  date: string;
  transactionCount: number;
  totalCommission: number;
  transactions: LedgerTransaction[];
}

interface CommissionLedgerModalProps {
  isOpen: boolean;
  onClose: () => void;
  storeApiKey: string;
}

export const CommissionLedgerModal: React.FC<CommissionLedgerModalProps> = ({ isOpen, onClose, storeApiKey }) => {
  const today = new Date().toISOString().slice(0, 10);
  const [selectedDate, setSelectedDate] = useState(today);
  const [data, setData] = useState<LedgerResponse | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [expandedTx, setExpandedTx] = useState<Set<number>>(new Set());

  const fetchLedger = useCallback(async (date: string) => {
    setLoading(true);
    setError(null);
    setExpandedTx(new Set());
    try {
      const res = await fetch(`/api/tenant/billing/commission-ledger?date=${date}`, {
        headers: { 'x-store-api-key': storeApiKey }
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to load ledger');
      setData(d);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [storeApiKey]);

  useEffect(() => {
    if (!isOpen) return;
    fetchLedger(selectedDate);
  }, [isOpen, selectedDate, fetchLedger]);

  const shiftDay = (delta: number) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + delta);
    setSelectedDate(d.toISOString().slice(0, 10));
  };

  const toggleExpand = (txId: number) => {
    setExpandedTx(prev => {
      const next = new Set(prev);
      if (next.has(txId)) next.delete(txId); else next.add(txId);
      return next;
    });
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-LK', { style: 'currency', currency: 'LKR', minimumFractionDigits: 2 }).format(amount);
  };

  const formatTime = (dateStr: string) => {
    return new Date(dateStr).toLocaleTimeString('en-LK', { hour: '2-digit', minute: '2-digit' });
  };

  const formatDateLabel = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('en-LK', { weekday: 'short', year: 'numeric', month: 'short', day: 'numeric' });
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4"
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="bg-slate-50 rounded-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-8 py-6 border-b border-slate-200 bg-white shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-red-100 border border-red-200 flex items-center justify-center">
              <TrendingDown className="text-red-600" size={20} />
            </div>
            <div>
              <h2 className="text-xl font-black text-slate-800">Commission Ledger</h2>
              <p className="text-xs text-slate-500 font-medium">Transaction-level breakdown of platform commissions</p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="w-10 h-10 rounded-lg hover:bg-slate-100 flex items-center justify-center text-slate-400 hover:text-slate-600 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Date Navigator */}
        <div className="flex items-center justify-center gap-4 px-8 py-4 border-b border-slate-200 bg-white/80 shrink-0">
          <button
            onClick={() => shiftDay(-1)}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors"
          >
            <ChevronLeft size={14} /> Previous Day
          </button>
          <div className="relative">
            <Calendar size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
            <input
              type="date"
              value={selectedDate}
              onChange={e => setSelectedDate(e.target.value)}
              max={today}
              className="pl-9 pr-4 py-1.5 rounded-lg bg-slate-100 border border-slate-200 text-slate-800 font-bold text-sm focus:outline-none focus:ring-2 focus:ring-red-500/30 focus:border-red-400"
            />
          </div>
          <button
            onClick={() => shiftDay(1)}
            disabled={selectedDate >= today}
            className="flex items-center gap-1 px-3 py-1.5 rounded-lg bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-xs transition-colors disabled:opacity-40"
          >
            Next Day <ChevronRight size={14} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-500"></div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <p className="text-red-600 font-bold">{error}</p>
              </div>
            </div>
          ) : !data || data.transactionCount === 0 ? (
            <div className="flex items-center justify-center py-20">
              <div className="text-center">
                <Receipt className="text-slate-300 mx-auto mb-3" size={48} />
                <p className="text-slate-500 font-medium">No transactions with commissions on {formatDateLabel(selectedDate)}</p>
              </div>
            </div>
          ) : (
            <div className="p-6">
              <div className="mb-4 flex items-center justify-between">
                <p className="text-sm text-slate-600 font-medium">
                  {data.transactionCount} transaction{data.transactionCount !== 1 ? 's' : ''} on {formatDateLabel(data.date)}
                </p>
                <div className="text-sm text-red-700 font-black">
                  Total Commission: {formatCurrency(data.totalCommission)}
                </div>
              </div>

              <div className="space-y-3">
                {data.transactions.map(tx => (
                  <div key={tx.id} className="bg-white rounded-xl border border-slate-200 overflow-hidden">
                    {/* Transaction header row — clickable */}
                    <button
                      onClick={() => toggleExpand(tx.id)}
                      className="w-full flex items-center gap-4 px-5 py-3.5 hover:bg-slate-50 transition-colors text-left"
                    >
                      <div className={`w-6 h-6 rounded-md flex items-center justify-center transition-transform ${expandedTx.has(tx.id) ? 'rotate-90' : ''}`}>
                        <ChevronRight size={16} className="text-slate-400" />
                      </div>
                      <div className="flex-1 grid grid-cols-5 gap-4 text-sm">
                        <div>
                          <div className="text-slate-800 font-bold">{formatTime(tx.createdAt)}</div>
                          <div className="text-slate-400 text-xs font-mono truncate max-w-[120px]">{tx.txRef}</div>
                        </div>
                        <div className="text-slate-700">
                          <span className="text-slate-400 text-xs block">Cashier</span>
                          {tx.cashierName}
                        </div>
                        <div className="text-right">
                          <span className="text-slate-400 text-xs block">Items</span>
                          <span className="font-bold">{tx.itemCount}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-slate-400 text-xs block">Bill Total</span>
                          <span className="font-bold">{formatCurrency(tx.total)}</span>
                        </div>
                        <div className="text-right">
                          <span className="text-slate-400 text-xs block">Commission</span>
                          <span className="text-red-600 font-bold">{formatCurrency(tx.totalCommission)}</span>
                        </div>
                      </div>
                    </button>

                    {/* Expanded item rows */}
                    {expandedTx.has(tx.id) && (
                      <div className="border-t border-slate-100">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="bg-slate-50 border-b border-slate-100">
                              <th className="text-left py-2 px-6 text-slate-500 font-bold uppercase tracking-wider text-[10px]">Item</th>
                              <th className="text-left py-2 px-3 text-slate-500 font-bold uppercase tracking-wider text-[10px]">Category</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold uppercase tracking-wider text-[10px]">Qty</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold uppercase tracking-wider text-[10px]">Unit Price</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold uppercase tracking-wider text-[10px]">Line Total</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold uppercase tracking-wider text-[10px]">Net Profit</th>
                              <th className="text-right py-2 px-3 text-slate-500 font-bold uppercase tracking-wider text-[10px]">Commission</th>
                            </tr>
                          </thead>
                          <tbody>
                            {tx.items.map((item, idx) => (
                              <tr key={item.id} className={`border-b border-slate-50 ${idx % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} hover:bg-blue-50/50 transition-colors`}>
                                <td className="py-2.5 px-6 text-slate-800 font-medium text-xs">
                                  <div className="flex items-center gap-2">
                                    <Package size={12} className="text-slate-400 shrink-0" />
                                    {item.productName}
                                  </div>
                                </td>
                                <td className="py-2.5 px-3 text-slate-500 text-xs">{item.category || '—'}</td>
                                <td className="py-2.5 px-3 text-right text-slate-700 font-mono text-xs">{item.qty.toFixed(2)}</td>
                                <td className="py-2.5 px-3 text-right text-slate-700 font-mono text-xs">{formatCurrency(item.unitPrice)}</td>
                                <td className="py-2.5 px-3 text-right text-slate-800 font-bold text-xs">{formatCurrency(item.lineTotal)}</td>
                                <td className="py-2.5 px-3 text-right text-xs">
                                  {item.netProfit !== null ? (
                                    <span className={item.netProfit >= 0 ? 'text-emerald-600 font-bold' : 'text-red-600 font-bold'}>
                                      {formatCurrency(item.netProfit)}
                                    </span>
                                  ) : (
                                    <span className="text-slate-400">—</span>
                                  )}
                                </td>
                                <td className="py-2.5 px-3 text-right text-xs">
                                  <span className="text-red-600 font-bold">{formatCurrency(item.commissionAmount)}</span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                        <div className="flex justify-end gap-8 px-6 py-2.5 bg-slate-50/80 border-t border-slate-100 text-xs">
                          <span className="text-slate-600 font-medium">
                            Bill Net Profit: <span className="text-emerald-600 font-bold">{formatCurrency(tx.totalNetProfit)}</span>
                          </span>
                          <span className="text-slate-600 font-medium">
                            Bill Commission: <span className="text-red-600 font-bold">{formatCurrency(tx.totalCommission)}</span>
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};